import type { ReactElement } from 'react';

/**
 * Inline SVG icons hand-copied from `design/mockups/admin/layout.html`
 * so the sidebar pixel-matches the mockup. Each component is plain
 * stateless SVG — colour + size driven by the parent SidebarItem
 * styling (currentColor / 16×16).
 */

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const DashboardIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="3" y="3" width="7" height="9" rx="2" />
    <rect x="14" y="3" width="7" height="5" rx="2" />
    <rect x="14" y="12" width="7" height="9" rx="2" />
    <rect x="3" y="16" width="7" height="5" rx="2" />
  </svg>
);

export const OrdersIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M3 7h18M3 12h18M3 17h12" />
  </svg>
);

export const ReportsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);

export const AppealsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M12 9v3M12 16h.01" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

export const PayoutsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

export const RefundsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 6v6l4 2" />
  </svg>
);

export const CardsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18M7 15h3" />
  </svg>
);

export const UsersIcon = (): ReactElement => (
  <svg {...svgProps}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
  </svg>
);

export const InspectorsIcon = UsersIcon;

export const HealthIcon = (): ReactElement => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Pulse-line icon — hand-copied from `design/mockups/admin/money-health.html`
// («Нужно внимание» sidebar row). The mockup draws a count chip next to it;
// that chip is deliberately NOT shipped — see `Sidebar.tsx`.
export const MoneyHealthIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M3 12h4l2-5 3 10 2.5-6 1.5 3h5" />
  </svg>
);

// Sliders/settings icon — hand-copied from the AUT-131 config mockup
// (`design/mockups/admin/config.html` sidebar row).
export const ConfigIcon = (): ReactElement => (
  <svg {...svgProps}>
    <line x1="4" y1="7" x2="20" y2="7" />
    <circle cx="9" cy="7" r="2.4" />
    <line x1="4" y1="17" x2="20" y2="17" />
    <circle cx="15" cy="17" r="2.4" />
  </svg>
);

export const SearchIcon = (): ReactElement => (
  <svg {...svgProps} strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

// Clipboard-with-check icon — hand-copied from `design/mockups/admin/pickers-list.html`
// («Подборщики» sidebar row, AUT-400).
export const PickersIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 4V3h6v1M9 13l2 2 4-4" />
  </svg>
);

// Office-building icon — hand-copied from `design/mockups/admin/companies-list.html`
// («Компании» sidebar row, AUT-403).
export const CompaniesIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
  </svg>
);

/** «Справочник авто» (AUT-437) — the mockup's car. */
export const VehicleCatalogIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M5 16l1.5-5.5A2 2 0 0 1 8.4 9h7.2a2 2 0 0 1 1.9 1.5L19 16" />
    <rect x="3" y="16" width="18" height="4" rx="1.5" />
    <path d="M7 20v1M17 20v1" />
  </svg>
);

// «Площадка» (AUT-542) — hand-copied from `design/mockups/sm-admin.html`.

/** «Специалисты» — the mockup's two people. */
export const SpecialistsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c.6-3.6 3.2-5.5 6.5-5.5s5.9 1.9 6.5 5.5M16 4.8a3.5 3.5 0 0 1 0 6.4M18 14.8c2 .7 3.2 2.4 3.5 5.2" />
  </svg>
);

/** «Объявления» — the mockup's car side view. */
export const CarListingsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M3 16v-3.5l2-5A2 2 0 0 1 6.9 6h10.2a2 2 0 0 1 1.9 1.5l2 5V16a1 1 0 0 1-1 1h-1M3 16a1 1 0 0 0 1 1h1M8 17h8M3 12.5h18" />
    <circle cx="6.5" cy="17" r="1.8" />
    <circle cx="17.5" cy="17" r="1.8" />
  </svg>
);

/** «Жалобы» — the mockup's flag. */
export const ContentReportsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
  </svg>
);

/** «Заявки» — the mockup's list sheet. */
export const CarRequestsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="4" y="3.5" width="16" height="17" rx="3" />
    <path d="M8 8.5h8M8 12h8M8 15.5h5" />
  </svg>
);

/** «Лиды» — an inbox tray. */
export const LeadsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M4 13.5 6.5 5h11L20 13.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18z" />
    <path d="M4 13.5h4.5a3.5 3.5 0 0 0 7 0H20" />
  </svg>
);

/** «Отзывы» — the mockup's star (`#i-star`), outlined like the other rows. */
export const ReviewsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="m12 2.8 2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" />
  </svg>
);

/** «Коллекции» — two stacked rails. */
export const CollectionsIcon = (): ReactElement => (
  <svg {...svgProps}>
    <rect x="3" y="4" width="18" height="6" rx="2" />
    <rect x="3" y="14" width="18" height="6" rx="2" />
  </svg>
);

/** «Цены РБ» — a price tag. */
export const RbPricesIcon = (): ReactElement => (
  <svg {...svgProps}>
    <path d="M3 12V4h8l10 10-8 8z" />
    <circle cx="7.5" cy="8.5" r="1" />
  </svg>
);
